import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Workflow, Lightbulb, Gauge, Cog, Laptop, BarChart, Zap, Target, Brain, CheckCircle, ArrowRight } from 'lucide-react';

interface TransformationProps {
  setCurrentPage: (page: string) => void;
}

const Transformation: React.FC<TransformationProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Workflow,
      title: "Process Optimization",
      description: "Streamline and enhance business processes for maximum efficiency.",
      items: [
        "Workflow automation",
        "Process analysis",
        "Efficiency optimization",
        "Performance monitoring"
      ]
    },
    {
      icon: Lightbulb,
      title: "Innovation Strategy",
      description: "Develop and implement innovative digital solutions.",
      items: [
        "Digital roadmap",
        "Technology assessment",
        "Innovation planning",
        "Change management"
      ]
    },
    {
      icon: Gauge,
      title: "Performance Enhancement",
      description: "Optimize system performance and operational efficiency.",
      items: [
        "System optimization",
        "Performance metrics",
        "Resource allocation",
        "Capacity planning"
      ]
    },
    {
      icon: Cog,
      title: "Automation",
      description: "Implement automated solutions to reduce manual effort.",
      items: [
        "Task automation",
        "Process automation",
        "Workflow integration",
        "Efficiency gains"
      ]
    }
  ];

  const services = [
    {
      icon: Brain,
      title: "Digital Strategy",
      description: "Comprehensive digital transformation strategy and roadmap.",
      items: [
        "Strategy development",
        "Digital assessment",
        "Technology planning",
        "Implementation roadmap"
      ]
    },
    {
      icon: Target,
      title: "Process Engineering",
      description: "Optimize and redesign business processes for digital efficiency.",
      items: [
        "Process analysis",
        "Workflow design",
        "System integration",
        "Performance metrics"
      ]
    },
    {
      icon: Zap,
      title: "Technology Integration",
      description: "Seamless integration of digital technologies and systems.",
      items: [
        "System integration",
        "Platform migration",
        "API development",
        "Data synchronization"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Laptop,
      title: "Digital Workplace",
      description: "Transform your workplace with modern digital tools.",
      features: [
        "Collaboration tools",
        "Digital workflows",
        "Remote capabilities",
        "Process automation"
      ]
    },
    {
      icon: BarChart,
      title: "Analytics & Insights",
      description: "Data-driven decision making and process improvement.",
      features: [
        "Performance analytics",
        "Process metrics",
        "Predictive insights",
        "Data visualization"
      ]
    },
    {
      icon: Workflow,
      title: "Process Excellence",
      description: "Achieve operational excellence through digital transformation.",
      features: [
        "Process optimization",
        "Quality improvement",
        "Efficiency gains",
        "Cost reduction"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <RefreshCw className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Digital Transformation</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Transform your business with innovative digital solutions and optimized processes for enhanced security and efficiency
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Transformation Services</h2>
            <p className="text-lg text-muted">
              Comprehensive digital transformation and process optimization services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{service.description}</p>

                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Digital Capabilities</h2>
            <p className="text-lg text-muted">
              Advanced capabilities for successful digital transformation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{capability.description}</p>

                <ul className="space-y-3">
                  {capability.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Transform Your Business?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Start your digital transformation journey with our expert solutions and services
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Transformation;